import type { ListBuilderConfiguration } from '../../types';
import { allRequestsList } from './lists/all-requests';
import { awaitingInfoList } from './lists/awaiting-info';
import { comingDueList } from './lists/coming-due';
import { fulfilledList } from './lists/fulfilled';
import { myRequestsList } from './lists/my-requests';
import { overdueList } from './lists/overdue';
import { pendingFulfillmentList } from './lists/pending-fulfillment';
import { pendingPoList } from './lists/pending-po';
import { pendingQuoteList } from './lists/pending-quote';
import { pendingReceiptList } from './lists/pending-receipt';
import { quoteInquiriesList } from './lists/quote-inquiries';

/**
 * Sourcing request list configuration with multi-view tabs
 * Workflow stages: All, My Requests, Coming Due, Overdue, Awaiting Info,
 * Quote Inquiries, Pending Quote, Pending PO, Pending Receipt,
 * Pending Fulfillment, Fulfilled
 *
 * Coming Due / Overdue surface target-date pressure: most users had no idea
 * SRs even carry a target date until these tabs surfaced it. Awaiting Info
 * surfaces SRs with at least one `pending_info` line so blocked work
 * doesn't disappear into the All view. All three inherit the All filter
 * set and lock their status/line-status filter so cancelled/fulfilled work
 * doesn't inflate the lane.
 */
export const SOURCING_REQUEST_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		allRequestsList,
		myRequestsList,
		comingDueList,
		overdueList,
		awaitingInfoList,
		quoteInquiriesList,
		pendingQuoteList,
		pendingPoList,
		pendingReceiptList,
		pendingFulfillmentList,
		fulfilledList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	/**
	 * Multi-key sorts that aren't pinned to a single column. Each entry
	 * is referenced by its `id` from a list's `defaultSortId`. We keep
	 * these table-level (not column-level) when the sort spans columns
	 * that wouldn't otherwise be sortable on their own.
	 */
	tableSorts: [
		{
			id: 'awaiting-info-first',
			name: 'Awaiting info first, then priority',
			description:
				'Surfaces SRs with pending-info lines at the top, then by priority and target date so blocked work is impossible to miss.',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lines_pending_info',
					objectTable: 'sourcing_request',
				},
				{
					direction: 'DESC',
					fieldName: 'priority',
					objectTable: 'sourcing_request',
				},
				{
					direction: 'ASC',
					fieldName: 'target_date',
					objectTable: 'sourcing_request',
				},
			],
		},
	],
	showResetButton: true,
	filters: {
		activeFilters: [
			'sourcingRequestKeywords',
			'sourcingRequestStatus',
			'sourcingRequestType',
			'sourcingRequestPriority',
			'sourcingRequestLineStatus',
			'sourcingRequestClient',
			'sourcingRequestAssignedUser',
			'sourcingRequestCreatedDateRange',
			'sourcingRequestTargetDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/sourcing-requests/:id',
			objectLabel: 'Sourcing Request',
			objectTable: 'sourcing_request',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: 'target_date', fieldLabel: 'Target Date' },
				{ fieldName: 'total_lines', fieldLabel: 'Total Lines' },
				{ fieldName: 'lines_fulfilled', fieldLabel: 'Lines Fulfilled' },
				{ fieldName: '_fk_client__id', fieldLabel: 'Client ID' },
				{ fieldName: 'clientName', fieldLabel: 'Client Name' },
				{ fieldName: '_fk_user__id', fieldLabel: 'Assigned User ID' },
				{ fieldName: 'assignedUserName', fieldLabel: 'Assigned User' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
			],
		},
	],
};
